import { type ClassValue, clsx } from 'clsx'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { twMerge } from 'tailwind-merge'
import { z } from 'zod'

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

function formatBrazilianDatetime(date: Date) {
  return format(date, 'dd/MM/yyyy - HH:mm')
}

function formatBrazilianDateLong(date: Date) {
  return format(date, "dd 'de' MMMM 'de' yyyy", { locale: ptBR })
}

function formatCurrency(value: number) {
  return value.toLocaleString('pt-br', {
    style: 'currency',
    currency: 'BRL',
  })
}

function formatToNumber(value: string) {
  if (!value.startsWith('R$')) return value

  return value
    .replace(/[R$\s]/g, '')
    .replace(/\./g, '')
    .replace(',', '.')
}

function convertToNumber(value?: string | null) {
  return Number(value?.replace(',', '.'))
}

function getDefaultsFromZod<T extends z.AnyZodObject>(schema: T) {
  const parseShape = (shape: z.ZodRawShape): Record<string, unknown> => {
    return Object.fromEntries(
      Object.entries(shape).map(([key, value]) => {
        if (value instanceof z.ZodDefault)
          return [key, value._def.defaultValue()]

        if (value instanceof z.ZodNullable) {
          const innerType = value._def.innerType
          if (innerType instanceof z.ZodObject)
            return [key, parseShape(innerType.shape)]

          return [key, '']
        }

        if (value instanceof z.ZodObject) return [key, parseShape(value.shape)]

        return [key, '']
      }),
    )
  }

  return parseShape(schema.shape)
}

function getURL(options?: {
  excludeSearchParams?: string[]
  withSearchParams?: Record<string, string>
}) {
  const href = window.location.href
  const url = new URL(href)

  options?.excludeSearchParams?.forEach((param) => {
    url.searchParams.delete(param)
  })

  Object.entries(options?.withSearchParams ?? {}).forEach((entry) => {
    const [name, value] = entry

    url.searchParams.set(name, value)
  })

  return url
}

function getFirstLetter(word: string) {
  return word.charAt(0).toUpperCase()
}

function slugify(word: string) {
  return word
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
}

function removeAccents(word: string) {
  return word.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

function removePropsFromObject(
  target: Record<string | number | symbol, unknown>,
  props: string[],
) {
  return Object.entries(target).reduce((result, item) => {
    if (!props.includes(item[0])) {
      return {
        ...result,
        [item[0]]: item[1],
      }
    }

    return result
  }, {})
}

// Workaround to resolve an issue with Radix Dialog: https://github.com/radix-ui/primitives/issues/1241
function resetBodyPointerEvents() {
  document.getElementsByTagName('body')[0].style.pointerEvents = ''
}

export {
  cn,
  formatBrazilianDatetime,
  formatBrazilianDateLong,
  formatCurrency,
  formatToNumber,
  convertToNumber,
  getDefaultsFromZod,
  getURL,
  getFirstLetter,
  slugify,
  removeAccents,
  removePropsFromObject,
  resetBodyPointerEvents,
}
